import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuro, MONTHS_EL_SHORT } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Save, Wallet, Sparkles } from "lucide-react";
import { AddLineButton } from "@/components/AddLineButton";
import { Link } from "react-router-dom";

const MONTH_KEYS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const SECTION_ORDER = ["revenue", "dept", "undistributed", "fixed"];

export default function PLBudget() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["owner", "hr"].includes(user?.role);
  const [year, setYear] = useState(2026);
  const [lines, setLines] = useState([]);
  const [srcYear, setSrcYear] = useState(2026);
  const [growth, setGrowth] = useState(5);

  const { data: meta } = useQuery({ queryKey: ["finance-accounts"], queryFn: async () => (await api.get("/finance/accounts")).data });
  const { isLoading } = useQuery({
    queryKey: ["pl-budget", year],
    queryFn: async () => {
      const d = (await api.get(`/finance/budget?year=${year}`)).data;
      setLines(d.lines);
      return d;
    },
  });

  const save = useMutation({
    mutationFn: async () => (await api.put("/finance/budget", {
      year,
      lines: lines.map((l) => ({ account_code: l.account_code, cost_center: l.cost_center, months: l.months })),
    })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["pl-report"] });
      toast.success(`Ο προϋπολογισμός P&L αποθηκεύτηκε — σύνολο ${fmtEuro(d.total)}`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const suggest = useMutation({
    mutationFn: async () => (await api.post(`/finance/budget/suggest?year=${year}&source_year=${srcYear}&growth_pct=${growth}`)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["pl-budget", year] });
      qc.invalidateQueries({ queryKey: ["pl-report"] });
      toast.success(`Πρόταση από ${d.source_year} +${d.growth_pct}% — ${d.lines} λογαριασμοί, σύνολο ${fmtEuro(d.total)}`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  useEffect(() => { document.title = "P&L Προϋπολογισμός — TAF"; }, []);

  const setCell = (i, m, v) => {
    const next = [...lines];
    next[i] = { ...next[i], months: { ...next[i].months, [m]: Number(v) || 0 } };
    setLines(next);
  };
  const setCc = (i, v) => {
    const next = [...lines];
    next[i] = { ...next[i], cost_center: v };
    setLines(next);
  };

  const rowTotal = (l) => MONTH_KEYS.reduce((s, m) => s + (Number(l.months?.[m]) || 0), 0);
  const sectionTotals = useMemo(() => {
    const t = {};
    lines.forEach((l) => { t[l.section] = (t[l.section] || 0) + rowTotal(l); });
    return t;
  }, [lines]);
  const gop = (sectionTotals.revenue || 0) - (sectionTotals.dept || 0) - (sectionTotals.undistributed || 0);
  const net = gop - (sectionTotals.fixed || 0);
  const cell = "w-[64px] rounded border border-[#E3DED4] px-1 py-0.5 text-[11px] text-right tabular outline-none focus:border-[#45423C]";

  return (
    <div className="space-y-6" data-testid="pl-budget-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">USALI · Uniform System of Accounts</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Προϋπολογισμός P&L</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Έσοδα & έξοδα ανά λογαριασμό, μήνα και cost center. Τα <b>Έσοδα</b> έρχονται αυτόματα από τον Προϋπολογισμό Εσόδων και η <b>Μισθοδοσία</b> από τον Προϋπολογισμό Μισθών (κατανομή TAF/TRIBE ανά εργαζόμενο). GOP και καθαρό αποτέλεσμα υπολογίζονται αυτόματα.</p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          {canEdit && <><AddLineButton kind="income" /><AddLineButton kind="expense" /></>}
          <div>
            <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Έτος</label>
            <select data-testid="pl-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="mt-1 block rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#45423C]">
              {[2026, 2027, 2028].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          {canEdit && (
            <div className="flex items-end gap-2 rounded-xl border border-[#B9AE9A]/40 bg-[#FFFBEB] px-3 py-2" data-testid="pl-suggest-box">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6F6A60] font-semibold">Από έτος</label>
                <input data-testid="pl-suggest-src" type="number" value={srcYear} onChange={(e) => setSrcYear(Number(e.target.value))}
                  className="mt-1 block w-20 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-sm text-right tabular outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6F6A60] font-semibold">Αύξηση %</label>
                <input data-testid="pl-suggest-growth" type="number" value={growth} onChange={(e) => setGrowth(Number(e.target.value))}
                  className="mt-1 block w-20 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-sm text-right tabular outline-none" />
              </div>
              <button data-testid="pl-suggest-btn" onClick={() => suggest.mutate()} disabled={suggest.isPending}
                className="inline-flex items-center gap-2 rounded-xl bg-[#C86235] text-white px-3 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                <Sparkles className="h-4 w-4" /> {suggest.isPending ? "Υπολογισμός…" : "Πρόταση από ιστορικό"}
              </button>
            </div>
          )}
          {canEdit && (
            <button data-testid="pl-budget-save" onClick={() => save.mutate()} disabled={save.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#6E7A63] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              <Save className="h-4 w-4" /> {save.isPending ? "Αποθήκευση…" : "Αποθήκευση"}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Έσοδα", sectionTotals.revenue], ["Έξοδα Τμημάτων", sectionTotals.dept],
          ["GOP", gop], ["Καθαρό Αποτέλεσμα", net]].map(([label, val]) => (
          <div key={label} className={`rounded-2xl p-5 ${label === "GOP" ? "bg-[#45423C] text-white" : "bg-white border border-[#E3DED4]"}`} data-testid={`pl-kpi-${label}`}>
            <div className={`text-[10px] uppercase tracking-widest ${label === "GOP" ? "text-[#8A8577]" : "text-[#6F6A60]"}`}>{label}</div>
            <div className="font-serif text-2xl mt-2 tabular">{fmtEuro(val || 0)}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center gap-2">
          <Wallet className="h-4 w-4 text-[#C86235]" />
          <h2 className="font-medium text-[#45423C]">Λογαριασμοί ανά μήνα</h2>
        </div>
        <div className="overflow-auto max-h-[70vh]">
          {isLoading ? <div className="p-10 text-center text-[#6F6A60]">Φόρτωση…</div> : (
            <table className="text-[11px] whitespace-nowrap w-full">
              <thead className="sticky top-0 z-20">
                <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                  <th className="px-2 py-2 sticky left-0 bg-[#45423C] z-30 w-[220px] max-w-[220px]">Λογαριασμός</th>
                  <th className="px-1 py-2">CC</th>
                  {MONTH_KEYS.map((m) => <th key={m} className="px-1 py-2 text-right">{MONTHS_EL_SHORT[Number(m)]}</th>)}
                  <th className="px-2 py-2 text-right">Σύνολο</th>
                </tr>
              </thead>
              {SECTION_ORDER.map((sec) => (
                <tbody key={sec}>
                  <tr className="bg-[#F1EDE6]">
                    <td colSpan={15} className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#45423C]">
                      {meta?.sections?.[sec] || sec} · {fmtEuro(sectionTotals[sec] || 0)}
                      {sec === "revenue" && <Link to={`/income-budget?year=${year}`} className="ml-3 normal-case tracking-normal font-medium text-[#C86235] underline" data-testid="pl-link-income-budget">από Προϋπολογισμό Εσόδων →</Link>}
                    </td>
                  </tr>
                  {lines.map((l, i) => l.section !== sec ? null : (
                    <tr key={l.account_code + l.cost_center + i} data-testid={`pl-row-${l.account_code}`} className={`border-t border-[#E3DED4] hover:bg-[#F7F5F1] ${l.locked ? "bg-[#FBF8F3]" : ""}`}>
                      <td className="px-2 py-1 sticky left-0 bg-inherit z-10 w-[220px] max-w-[220px] truncate" title={`${l.account_code} ${l.name}`}>
                        <span className="font-mono text-[10px] text-[#6F6A60] mr-1.5">{l.account_code}</span>
                        <span className="text-[#45423C]">{l.name}</span>
                        {l.source === "payroll_budget" && <Link to={`/budget?year=${year}`} className="ml-1.5 text-[9px] uppercase tracking-wider text-[#C86235]" title="Από Προϋπολογισμό Μισθών" data-testid={`pl-src-pay-${l.account_code}-${l.cost_center}`}>μισθοί</Link>}
                      </td>
                      <td className="px-1 py-1">
                        {canEdit && !l.locked ? (
                          <select data-testid={`pl-cc-${l.account_code}`} value={l.cost_center} onChange={(e) => setCc(i, e.target.value)}
                            className="rounded border border-[#E3DED4] px-0.5 py-0.5 text-[11px] outline-none">
                            {(meta?.cost_centers || []).map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                          </select>
                        ) : <span className="text-[#6F6A60]">{l.cost_center}</span>}
                      </td>
                      {MONTH_KEYS.map((m) => (
                        <td key={m} className="px-1 py-1 text-right tabular">
                          {canEdit && !l.locked ? (
                            <input data-testid={`pl-cell-${l.account_code}-${m}`} type="number" className={cell}
                              value={l.months?.[m] ?? 0} onChange={(e) => setCell(i, m, e.target.value)} />
                          ) : <span className={l.locked ? "text-[#6F6A60]" : ""}>{fmtEuro(l.months?.[m]).replace("€", "").trim()}</span>}
                        </td>
                      ))}
                      <td className="px-2 py-1 text-right tabular font-semibold text-[#45423C]">{fmtEuro(rowTotal(l))}</td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
